import React, { useState } from 'react';
import { Heart, Gift, Star, Check } from 'lucide-react';
import JourneyHeader from './JourneyHeader';
import Footer from './Footer';

const Donations = () => {
  const [selectedTier, setSelectedTier] = useState(null);

  const donationTiers = [
    {
      level: 'Level 1',
      name: 'SUPPORTER',
      amount: '$10-$49',
      monthlyAmount: '$10',
      color: 'from-sacred-teal to-blue-500',
      icon: '🌱',
      benefits: [
        'Monthly newsletter with exclusive insights',
        'Digital thank you card',
        'Access to community forum',
        'Early notifications of new content',
        'Member badge on profile'
      ]
    },
    {
      level: 'Level 2',
      name: 'ADVOCATE',
      amount: '$50-$149',
      monthlyAmount: '$50',
      color: 'from-fire-main to-fire-accent',
      icon: '🔥',
      popular: true,
      benefits: [
        'All Level 1 benefits',
        '15% discount on INSPIYAH CULTURE merch',
        'Exclusive monthly webinar access',
        'Personalized spiritual guidance session (quarterly)',
        'Priority access to workshops and events',
        'Featured member spotlight opportunity',
        'Exclusive content library access',
        'Monthly Q&A with Keeper Of The Fiyah'
      ]
    },
    {
      level: 'Level 3',
      name: 'VISIONARY',
      amount: '$150+',
      monthlyAmount: '$150',
      color: 'from-brand-gold to-yellow-500',
      icon: '✨',
      benefits: [
        'All Level 1 & 2 benefits',
        '25% discount on all INSPIYAH products',
        'Monthly 1-on-1 coaching session',
        'VIP access to all events and retreats',
        'Exclusive INSPIYAH CULTURE limited edition items',
        'Recognition on our donor wall',
        'Co-create content opportunities',
        'Lifetime community membership',
        'Direct line to leadership team',
        'Annual appreciation gift package'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      <JourneyHeader activeSection="donations" />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <div className="mb-8">
            <Heart size={64} className="mx-auto text-fire-main mb-4" />
          </div>
          <h1 className="text-6xl md:text-7xl font-bold bg-gradient-to-r from-brand-deep via-fire-main to-sacred-teal bg-clip-text text-transparent mb-6">
            Support The Mission
          </h1>
          <p className="text-2xl text-gray-600 max-w-3xl mx-auto mb-8">
            Your generosity fuels our mission to uplift, enlighten, and empower communities worldwide.
          </p>
          <div className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-brand-deep/10 to-fire-main/10 rounded-full">
            <Star className="text-brand-gold fill-brand-gold" size={20} />
            <span className="font-semibold text-gray-700">Choose a tier that resonates with your spirit</span>
          </div>
        </div>
      </section>

      {/* Donation Tiers */}
      <section className="pb-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-3 gap-8">
            {donationTiers.map((tier, index) => (
              <div
                key={index}
                onClick={() => setSelectedTier(tier.level)}
                className={`relative bg-white rounded-3xl p-8 cursor-pointer transition-all duration-300 hover:shadow-2xl hover:scale-105 ${
                  selectedTier === tier.level 
                    ? 'shadow-2xl scale-105 ring-4 ring-brand-gold/30' 
                    : 'shadow-lg'
                } ${tier.popular ? 'border-2 border-brand-gold' : 'border-2 border-gray-200'}`}
              >
                {tier.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="px-6 py-2 bg-gradient-to-r from-brand-gold to-fire-accent text-white text-sm font-bold rounded-full shadow-lg uppercase">
                      Most Popular
                    </span>
                  </div>
                )}

                <div className="text-center mb-8">
                  <div className="text-7xl mb-4">{tier.icon}</div>
                  <div className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-2">{tier.level}</div>
                  <h3 className="text-3xl font-bold mb-3 text-gray-900">{tier.name}</h3>
                  <div className={`text-5xl font-bold bg-gradient-to-r ${tier.color} bg-clip-text text-transparent mb-2`}>
                    {tier.amount}
                  </div>
                  <div className="text-sm text-gray-500">per month</div>
                </div>

                <ul className="space-y-4 mb-8">
                  {tier.benefits.map((benefit, i) => (
                    <li key={i} className="flex items-start space-x-3">
                      <div className="flex-shrink-0 mt-0.5">
                        <div className="w-5 h-5 bg-gradient-to-br from-brand-deep to-fire-main rounded-full flex items-center justify-center">
                          <Check size={14} className="text-white" />
                        </div>
                      </div>
                      <span className="text-gray-700 text-sm leading-relaxed">{benefit}</span>
                    </li>
                  ))}
                </ul>

                <button 
                  className={`w-full py-4 rounded-full font-bold text-lg transition-all duration-300 ${
                    tier.popular 
                      ? 'bg-gradient-to-r from-brand-gold to-fire-accent text-white hover:shadow-xl hover:scale-105' 
                      : 'bg-gradient-to-r from-brand-deep to-fire-main text-white hover:shadow-xl hover:scale-105'
                  }`}
                >
                  Choose {tier.name}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* One-Time Donation */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <div className="bg-gradient-to-r from-brand-deep via-fire-main to-sacred-teal rounded-3xl p-12 text-center text-white shadow-2xl">
            <Gift size={48} className="mx-auto mb-6" />
            <h3 className="text-4xl font-bold mb-4">Prefer a One-Time Donation?</h3>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Every contribution, no matter the size, makes a meaningful impact on our mission to spread light and transformation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              {['$25', '$50', '$100', '$250'].map((amount) => (
                <button
                  key={amount}
                  className="px-8 py-3 bg-white/20 backdrop-blur-sm text-white font-semibold rounded-full border-2 border-white/50 hover:bg-white hover:text-brand-deep transition-all duration-300"
                >
                  {amount}
                </button>
              ))}
            </div>
            <button className="px-12 py-4 bg-white text-brand-deep font-bold rounded-full hover:bg-brand-gold hover:text-white transition-all duration-300 hover:shadow-2xl text-lg">
              Make a Custom Donation
            </button>
          </div>
        </div>
      </section>

      {/* Impact Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <h2 className="text-4xl font-bold text-center mb-12 bg-gradient-to-r from-brand-deep to-fire-main bg-clip-text text-transparent">
            Your Impact
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-8">
              <div className="text-5xl font-bold text-brand-deep mb-2">10,000+</div>
              <div className="text-gray-600 text-lg">Lives Transformed</div>
            </div>
            <div className="text-center p-8">
              <div className="text-5xl font-bold text-fire-main mb-2">50+</div>
              <div className="text-gray-600 text-lg">Community Events</div>
            </div>
            <div className="text-center p-8">
              <div className="text-5xl font-bold text-sacred-teal mb-2">$250K+</div>
              <div className="text-gray-600 text-lg">Raised This Year</div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Donations;